import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

/**
 * Identifier for the viewport grid store type.
 */
const PRESENTATION_TYPE_ID = 'userSetting';

/**
 * Flag to enable or disable debug mode for the store.
 * Set to `true` to enable zustand devtools.
 */
const DEBUG_STORE = false;

/**
 * Represents the state of the viewport grid.
 */
type UserSettingState = {
	[key: string]: unknown;
};

/**
 * State shape for the User Setting store.
 */
type UserSettingStore = {
	/**
	 * Type identifier for the store.
	 */
	type: string;

	/**
	 * Stores the viewport grid state as a key-value mapping.
	 */
	state: UserSettingState;

	/**
	 * Sets the viewport grid state for a given key.
	 *
	 * @param key - The key to set in the viewport grid state.
	 * @param value - The value to associate with the key.
	 */
	setUserSettingState: (key: string, value: unknown) => void;

	/**
	 * Clears the entire viewport grid state.
	 */
	clearUserSettingState: () => void;

	toggleHidePatientInfo: () => void;
};

/**
 * Creates the Viewport Grid store.
 *
 * @param set - The zustand set function.
 * @returns The User Setting store state and actions.
 */
const createUserSettingStore = (set): UserSettingStore => ({
	type: PRESENTATION_TYPE_ID,
	state: {},

	/**
	 * Sets the user setting state for a given key.
	 */
	setUserSettingState: (key, value) =>
		set(
			(store) => ({
				state: {
					...store.state,
					[key]: value
				}
			}),
			false,
			'setUserSettingState'
		),

	/**
	 * Clears the entire user setting state.
	 */
	clearUserSettingState: () => set({ state: {} }, false, 'clearUserSettingState'),

	toggleHidePatientInfo: () =>
		set(
			(store) => ({
				state: {
					...store.state,
					hidePatientInfo: !store.state.hidePatientInfo
				}
			}),
			false,
			'toggleHidePatientInfo'
		)
});

/**
 * Zustand store for managing user setting state.
 * Applies devtools middleware when DEBUG_STORE is enabled.
 */
export const useUserSettingStore = create<UserSettingStore>()(
	DEBUG_STORE ? devtools(createUserSettingStore, { name: 'UserSettingStore' }) : createUserSettingStore
);
